import { useQueryClient } from '@tanstack/react-query'
import { TrashIcon } from 'lucide-react'
import React, { useState } from 'react'

import { useDeleteProofOfCoverage } from 'components/hooks/member_onboarding_v2/useDeleteProofOfCoverage'
import { useUploadProofOfCoverage } from 'components/hooks/useUploadProofOfCoverage'
import { FileList, FileUpload, IconButton } from 'slab'
import { getGetMemberOnboardingProofOfCoverageMostRecentQueryKey as proofOfCoverageQueryKey } from 'utils/web_api'
import { type MemberOnboardingProofOfCoverage } from 'utils/web_api.schemas'

import { useOnboarding } from '../OnboardingContext'

const FileUploadComponent = ({
  proofOfCoverage,
  planYear,
}: {
  proofOfCoverage: MemberOnboardingProofOfCoverage | undefined;
  planYear: string;
}) => {
  const [error, setError] = useState<string | null>(null)
  const { refreshOnboardingInfo } = useOnboarding()

  const queryClient = useQueryClient()

  const uploadProofOfCoverage = useUploadProofOfCoverage({
    planYear,
  })
  const deleteProofOfCoverage = useDeleteProofOfCoverage({
    fileId: proofOfCoverage?.file_id ?? '',
  })

  const validProofOfCoverage =
    proofOfCoverage && !!proofOfCoverage.file_id.length

  const refreshProofOfCoverage = () => {
    queryClient.invalidateQueries({
      queryKey: proofOfCoverageQueryKey(),
    })
  }

  const onRemoveFile = () => {
    if (proofOfCoverage?.file_id) {
      deleteProofOfCoverage.mutate(undefined, {
        onSuccess: () => {
          refreshProofOfCoverage()
          refreshOnboardingInfo()
        },
        onError: () => {
          setError('Something went wrong deleting the file. Please try again.')
        },
      })
    }
  }

  const onFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList?.length) return false

    const newFile = fileList[0]
    uploadProofOfCoverage.mutate(
      {
        file: newFile,
      },
      {
        onSuccess: () => {
          refreshProofOfCoverage()
          refreshOnboardingInfo()
        },
        onError: () => {
          setError(
            'Something went wrong uploading the file. Please try again.'
          )
        },
      }
    )
  }

  return (
    <div>
      {!validProofOfCoverage && (
        <FileUpload
          multiple={false}
          error={error}
          accept='.pdf,.jpg,.jpeg,.png'
          onChange={onFileUpload}
        >
          <FileUpload.Content layout='horizontal' />
        </FileUpload>
      )}

      {validProofOfCoverage && (
        <FileList>
          <FileList.File
            name={proofOfCoverage.file_name}
            status='success'
            actions={
              <IconButton
                icon={TrashIcon}
                onClick={onRemoveFile}
                label='Remove'
              />
            }
          />
        </FileList>
      )}
    </div>
  )
}

export default FileUploadComponent
